import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Loading, AccuracyBar } from "@/components/ui-kit";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { TrendingUp, TrendingDown, ArrowLeft, MessageSquare, Phone, BookOpen, Check } from "lucide-react";

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) + " · " +
      d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

export default function StudentProfile() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [assign, setAssign] = useState(null);
  const [selAssign, setSelAssign] = useState(new Set());
  const [savingAssign, setSavingAssign] = useState(false);
  useEffect(() => { api.get(`/tutor/students/${id}`).then((r) => setP(r.data)); }, [id]);
  useEffect(() => {
    api.get(`/tutor/students/${id}/assignable-homework`).then((r) => {
      setAssign(r.data);
      setSelAssign(new Set(r.data.items.filter((i) => i.assigned).map((i) => i.id)));
    }).catch(() => setAssign({ items: [] }));
  }, [id]);
  if (!p) return <Loading />;

  const toggleAssign = (hid) => setSelAssign((prev) => {
    const n = new Set(prev);
    n.has(hid) ? n.delete(hid) : n.add(hid);
    return n;
  });
  const saveAssign = async () => {
    setSavingAssign(true);
    try {
      await api.post(`/tutor/students/${id}/assign-homework`, { assigned_ids: [...selAssign] });
      setAssign((a) => ({ ...a, items: a.items.map((i) => ({ ...i, assigned: selAssign.has(i.id) })) }));
      toast.success("Homework assignments updated.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not update assignments."); }
    finally { setSavingAssign(false); }
  };

  return (
    <div className="animate-fade-up">
      <Link to="/tutor/students" className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#7C3AED] hover:underline">
        <ArrowLeft className="h-4 w-4" /> Back to students
      </Link>
      <div className="ember-card mb-6 flex flex-wrap items-center gap-4 p-6">
        <Avatar className="h-16 w-16">
          <AvatarImage src={p.avatar_url || undefined} />
          <AvatarFallback className="bg-[#EDE9FE] text-[#5B21B6] text-lg font-bold">{p.name.split(" ").map((x) => x[0]).join("")}</AvatarFallback>
        </Avatar>
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-[#1E1B2E]">{p.name}</h1>
          <p className="text-sm text-[#524B6B]">{p.email} · {p.program_name}</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="ember-card p-6">
          <h2 className="font-heading text-lg font-bold text-[#1E1B2E]">Recent performance</h2>
          <div className="mt-4 space-y-3">
            {p.recent.length === 0 && <p className="text-sm text-[#948EAB]">No completed assessments yet.</p>}
            {p.recent.map((r) => (
              <Link key={r.attempt_id} to={`/student/result/${r.attempt_id}`} className="flex items-center justify-between rounded-xl border border-[#EEEAF8] px-4 py-3 hover:bg-[#FAF9FE]">
                <div>
                  <div className="text-sm font-bold text-[#1E1B2E]">{r.title || r.section_name}</div>
                </div>
                <span className="font-heading font-extrabold text-[#5B21B6]">{Math.round(r.percentage)}%</span>
              </Link>
            ))}
          </div>
        </div>

        <div className="ember-card p-6">
          <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-[#1E1B2E]"><TrendingUp className="h-4 w-4 text-[#059669]" /> Strongest areas</h2>
          <div className="flex flex-wrap gap-2">
            {p.strong.map((s) => <span key={s} className="rounded-full bg-[#ECFDF5] px-3 py-1.5 text-sm font-semibold text-[#059669]">{s}</span>)}
          </div>
          <h2 className="mb-3 mt-6 flex items-center gap-2 font-heading text-lg font-bold text-[#1E1B2E]"><TrendingDown className="h-4 w-4 text-[#E11D48]" /> Requires attention</h2>
          <div className="flex flex-wrap gap-2">
            {p.weak.map((s) => <span key={s} className="rounded-full bg-[#FFF1F2] px-3 py-1.5 text-sm font-semibold text-[#E11D48]">{s}</span>)}
          </div>
        </div>

        <div className="ember-card p-6">
          <h2 className="font-heading text-lg font-bold text-[#1E1B2E]">Overall by section</h2>
          <div className="mt-4 space-y-4">
            {p.progress.map((sec) => (
              <div key={sec.section}>
                <div className="mb-1.5 flex justify-between text-sm"><span className="font-semibold text-[#1E1B2E]">{sec.section_name}</span><span className="font-bold text-[#5B21B6]">{sec.overall}%</span></div>
                <AccuracyBar pct={sec.overall} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {assign && assign.items.length > 0 && (
        <div className="ember-card mt-6 p-6" data-testid="assign-homework-card">
          <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-[#1E1B2E]"><BookOpen className="h-5 w-5 text-[#5B21B6]" /> Assigned homework</h3>
            <Button size="sm" onClick={saveAssign} disabled={savingAssign} data-testid="save-assign-homework" className="h-9 rounded-xl ember-gradient text-xs font-bold">{savingAssign ? "Saving…" : "Save assignments"}</Button>
          </div>
          <p className="mb-3 text-xs text-[#948EAB]">Tick any released {assign.program_name} homework to give this student access — useful when they join after earlier work was set.</p>
          <div className="max-h-[360px] space-y-2 overflow-y-auto pr-1">
            {assign.items.map((it) => {
              const on = selAssign.has(it.id);
              return (
                <button type="button" key={it.id} data-testid={`assign-hw-${it.id}`} onClick={() => toggleAssign(it.id)}
                  className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition-colors ${on ? "border-[#7C3AED] bg-[#F5F3FF]" : "border-[#EEEAF8] hover:bg-[#FAF9FE]"}`}>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-bold text-[#1E1B2E]">{it.title || it.section_name}</div>
                    <div className="truncate text-xs text-[#948EAB]">{it.title}</div>
                  </div>
                  <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full border ${on ? "border-[#7C3AED] bg-[#7C3AED] text-white" : "border-[#D8D0EC] text-transparent"}`}><Check className="h-3.5 w-3.5" /></span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {p.progress.map((sec) => sec.timeline.length > 1 && (
        <div key={sec.section} className="ember-card mt-6 p-6">
          <h3 className="mb-4 font-heading text-lg font-bold text-[#1E1B2E]">{sec.section_name} — progress over time</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={sec.timeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1EEFA" />
              <XAxis dataKey="week" stroke="#948EAB" fontSize={12} />
              <YAxis domain={[0, 100]} stroke="#948EAB" fontSize={12} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E9E5F5" }} />
              <Line type="monotone" dataKey="percentage" stroke="#7C3AED" strokeWidth={3} dot={{ r: 4, fill: "#7C3AED" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ))}

      {p.show_parent_sms && (
      <div className="ember-card mt-6 p-6" data-testid="parent-sms-history">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-[#1E1B2E]">
            <MessageSquare className="h-4 w-4 text-[#5B21B6]" /> Parent SMS history
          </h3>
          {p.parent_phone ? (
            <span className="inline-flex items-center gap-1.5 text-sm text-[#524B6B]">
              {p.parent_name && <span className="font-semibold">{p.parent_name}</span>}
              <span className="inline-flex items-center gap-1 text-[#5B21B6]"><Phone className="h-3.5 w-3.5" />{p.parent_phone}</span>
            </span>
          ) : (
            <Link to="/tutor/payments" className="text-sm font-semibold text-[#7C3AED] hover:underline">Add parent contact</Link>
          )}
        </div>
        {(!p.sms_history || p.sms_history.length === 0) ? (
          <p className="text-sm text-[#948EAB]">No messages sent to this parent yet. Send updates from the Communications page.</p>
        ) : (
          <div className="space-y-3">
            {p.sms_history.map((s) => (
              <div key={s.id} data-testid={`sms-log-${s.id}`} className="rounded-xl border border-[#EEEAF8] bg-[#FAF9FE] p-4">
                <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2 text-xs text-[#948EAB]">
                  <span className="font-semibold text-[#524B6B]">Sent {fmtWhen(s.sent_at)}</span>
                  <span>to {s.parent_phone}{s.sent_by_name ? ` · by ${s.sent_by_name}` : ""}</span>
                </div>
                <p className="whitespace-pre-wrap text-sm text-[#1E1B2E]">{s.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
